import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import type React from "react"
import { useMemo, useState } from "react"
import { z } from "zod"

import { EnrollmentActionsMenu } from "@/components/layout/Common/EnrollmentActionsMenu"
import { InvoiceDetailModal } from "@/components/modals/InvoiceDetailModal"
import { EnrollmentCard } from "@/components/molecules/EnrollmentCard"
import { SwimEmptyState } from "@/components/molecules/SwimEmptyState"
import { type ViewMode, ViewToggle } from "@/components/molecules/ViewToggle"
import {
  DataTable,
  type DataTableColumn,
} from "@/components/organisms/DataTable"
import { AddEnrollment } from "@/features/enrollments"
import { useClassTemplate } from "@/hooks/api/useClassTemplates"
import { usePermissions } from "@/hooks/usePermissions"
import { useIsMobile } from "@/hooks/useResponsive"
import { api } from "@/services/api"
import type {
  EnrollmentListResponse,
  EnrollmentResponse,
} from "@/types/enrollment"
import { unwrapStandardResponse } from "@/utils/apiResponse"

const enrollmentsSearchSchema = z.object({
  page: z.coerce.number().min(1).catch(1),
  search: z.string().optional(),
  sort: z.enum(["created_at", "updated_at"]).catch("created_at"),
  direction: z.enum(["asc", "desc"]).catch("desc"),
  class_template_id: z.string().optional(),
})

const PER_PAGE = 25
type SortField = "created_at" | "updated_at"
type SortDirection = "asc" | "desc"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

const formatCurrency = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined) return "N/A"
  return `$${Number(amount).toFixed(2)}`
}

function getEnrollmentsQueryOptions({
  page,
  search,
  sort,
  direction,
  classTemplateId,
}: {
  page: number
  search?: string
  sort: SortField
  direction: SortDirection
  classTemplateId?: string
}) {
  const debugEnrollments =
    import.meta.env.DEV && import.meta.env.VITE_DEBUG_ENROLLMENTS === "true"

  return {
    queryFn: async () => {
      if (debugEnrollments) {
        console.log(
          `Enrollments page: Fetching page ${page} with ${PER_PAGE} records per page`,
        )
      }

      const response = await api.enrollments.readEnrollmentsApiV1EnrollmentsGet(
        {
          skip: (page - 1) * PER_PAGE,
          limit: PER_PAGE,
          search,
          sortBy: sort,
          sortOrder: direction,
          classTemplateId,
        },
      )

      const payload =
        unwrapStandardResponse<EnrollmentListResponse>(response)
      const items = payload?.items ?? []
      const total = payload?.total ?? 0

      if (debugEnrollments) {
        console.log("Enrollments page: API payload:", payload)
        console.log("🔍 Enrollments page: items count:", items.length)
        console.log("🔍 Enrollments page: total:", total)
      }

      return {
        data: items,
        count: total,
      }
    },
    queryKey: [
      "enrollments",
      {
        page,
        search,
        sort,
        direction,
        classTemplateId,
      },
    ],
  }
}

export const Route = createFileRoute("/_layout/enrollments")({
  component: Enrollments,
  validateSearch: (search) => enrollmentsSearchSchema.parse(search),
})

function Enrollments() {
  const navigate = useNavigate({ from: Route.fullPath })
  const { page, search, sort, direction, class_template_id } = Route.useSearch()
  const { hasPermission } = usePermissions()
  const [searchInput, setSearchInput] = useState(search ?? "")
  // Default to cards for parents, table for staff
  const isParentView =
    hasPermission("enrollments:read_own") && !hasPermission("enrollments:read")
  const [viewMode, setViewMode] = useState<ViewMode>(
    isParentView ? "cards" : "table",
  )
  const isMobile = useIsMobile()
  const effectiveViewMode = isMobile ? "cards" : viewMode

  // Fetch class template details when filtered by a specific class
  const { data: classTemplate } = useClassTemplate(class_template_id ?? "")

  const { data, isLoading, isPlaceholderData } = useQuery({
    ...getEnrollmentsQueryOptions({
      page,
      search: search || undefined,
      sort,
      direction,
      classTemplateId: class_template_id,
    }),
    placeholderData: (previous) => previous,
  })

  const setPage = (nextPage: number) =>
    navigate({
      search: (previous) => ({
        ...previous,
        page: nextPage,
      }),
    })

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const nextValue = searchInput.trim()
    navigate({
      search: (previous) => {
        const { search: _prevSearch, ...rest } = previous
        return nextValue
          ? { ...rest, page: 1, search: nextValue }
          : { ...rest, page: 1 }
      },
    })
  }

  const handleSortChange = (nextSort: SortField) => {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        sort: nextSort,
      }),
    })
  }

  const handleDirectionChange = (nextDirection: SortDirection) => {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        direction: nextDirection,
      }),
    })
  }

  const records = data?.data?.slice(0, PER_PAGE) ?? []
  const count = data?.count ?? 0
  const showActions =
    hasPermission("enrollments:update") || hasPermission("enrollments:delete")

  // Determine if user is admin/staff (has full management permissions)
  const isAdminView =
    hasPermission("enrollments:update") || hasPermission("system:admin")

  const [selectedInvoice, setSelectedInvoice] =
    useState<EnrollmentResponse | null>(null)

  const columns = useMemo<DataTableColumn<EnrollmentResponse>[]>(() => {
    const tableColumns: DataTableColumn<EnrollmentResponse>[] = []

    // User-friendly columns showing names instead of IDs
    tableColumns.push({
      key: "student_name",
      label: "Student",
      render: (enrollment) => (
        <Text fontWeight="medium">
          {enrollment.student_name ||
            formatFieldValue(enrollment.student_id, "str")}
        </Text>
      ),
    })

    tableColumns.push({
      key: "class_name",
      label: "Class",
      render: (enrollment) => (
        <Text>{enrollment.class_name || "N/A"}</Text>
      ),
    })

    tableColumns.push({
      key: "session_name",
      label: "Season",
      render: (enrollment) => (
        <Text>{enrollment.session_name || "N/A"}</Text>
      ),
    })

    tableColumns.push({
      key: "status",
      label: "Status",
      render: (enrollment) => {
        const status = enrollment.status || "pending"
        const colorScheme =
          status === "confirmed"
            ? "green"
            : status === "pending"
              ? "yellow"
              : status === "waitlisted"
                ? "blue"
                : status === "cancelled"
                  ? "red"
                  : status === "completed"
                    ? "purple"
                    : status === "withdrawn"
                      ? "gray"
                      : "gray"

        return (
          <Box
            as="span"
            px={2}
            py={1}
            borderRadius="md"
            fontSize="xs"
            fontWeight="medium"
            bg={`${colorScheme}.50`}
            color={`${colorScheme}.700`}
          >
            {status}
          </Box>
        )
      },
    })

    tableColumns.push({
      key: "payment_status",
      label: "Payment",
      render: (enrollment) => {
        const paymentStatus = enrollment.invoice?.payment_status || "unpaid"
        const colorScheme =
          paymentStatus === "paid"
            ? "green"
            : paymentStatus === "partial"
              ? "orange"
              : paymentStatus === "unpaid"
                ? "red"
                : paymentStatus === "refunded"
                  ? "purple"
                  : "gray"

        return (
          <Box
            as="span"
            px={2}
            py={1}
            borderRadius="md"
            fontSize="xs"
            fontWeight="medium"
            bg={`${colorScheme}.50`}
            color={`${colorScheme}.700`}
          >
            {paymentStatus}
          </Box>
        )
      },
    })

    tableColumns.push({
      key: "amount_due",
      label: "Amount Due",
      render: (enrollment) => (
        <Text>{formatCurrency(enrollment.invoice?.amount_due)}</Text>
      ),
    })

    tableColumns.push({
      key: "amount_paid",
      label: "Amount Paid",
      render: (enrollment) => (
        <Text>{formatCurrency(enrollment.invoice?.amount_paid)}</Text>
      ),
    })

    // Admin-only columns
    if (isAdminView) {
      tableColumns.push({
        key: "payment_method",
        label: "Payment Method",
        render: (enrollment) => (
          <Text>
            {formatFieldValue(enrollment.invoice?.payment_method, "str")}
          </Text>
        ),
      })

      tableColumns.push({
        key: "notes",
        label: "Notes",
        render: (enrollment) => (
          <Text truncate maxW="200px">
            {formatFieldValue(enrollment.notes, "str")}
          </Text>
        ),
      })
    }

    if (showActions) {
      tableColumns.push({
        key: "actions",
        label: "Actions",
        render: (enrollment) => (
          <EnrollmentActionsMenu enrollment={enrollment} />
        ),
      })
    }

    return tableColumns
  }, [showActions, isAdminView])

  if (isLoading) {
    return (
      <Container maxW="full">
        <Flex justifyContent="space-between" alignItems="center" pt={12} mb={6}>
          <Heading size="lg">Class Enrollments</Heading>
        </Flex>
        <Flex
          alignItems="center"
          justifyContent="center"
          direction="column"
          gap={4}
          minH="240px"
        >
          <Spinner size="lg" color="blue.500" />
          <Text color="gray.600">Loading enrollments...</Text>
        </Flex>
      </Container>
    )
  }

  const clearClassTemplateFilter = () => {
    navigate({
      search: (previous) => {
        const { class_template_id: _, ...rest } = previous
        return { ...rest, page: 1 }
      },
    })
  }

  return (
    <Container maxW="full">
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        pt={12}
        mb={6}
        gap={{ base: 3, md: 0 }}
      >
        <Flex justify="space-between" align="center">
          <VStack alignItems="flex-start" gap={1}>
            <Heading size="lg">
              {isAdminView ? "Class Enrollments" : "My Enrollments"}
            </Heading>
            {class_template_id && (
              <HStack gap={2}>
                <Box
                  px={2}
                  py={1}
                  bg="blue.50"
                  color="blue.700"
                  borderRadius="md"
                  fontSize="sm"
                >
                  Filtered by class
                </Box>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={clearClassTemplateFilter}
                >
                  Clear filter
                </Button>
              </HStack>
            )}
          </VStack>
          <Box display={{ base: "block", md: "none" }}>
            {hasPermission("enrollments:create") && <AddEnrollment />}
          </Box>
        </Flex>
        <Flex
          direction={{ base: "column", md: "row" }}
          gap={{ base: 3, md: 4 }}
          align={{ base: "stretch", md: "center" }}
        >
          <form onSubmit={handleSearchSubmit}>
            <HStack gap={2}>
              <Input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search enrollments"
                size="sm"
                width={{ base: "full", md: "220px" }}
                aria-label="Search enrollments"
              />
              <Button type="submit" size="sm" colorPalette="blue">
                Search
              </Button>
            </HStack>
          </form>
          <HStack gap={2}>
            <Box
              {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
              as="select"
              size="sm"
              value={sort}
              onChange={(event) =>
                handleSortChange(
                  (event.target as HTMLSelectElement).value as SortField,
                )
              }
              aria-label="Sort enrollments"
              width={{ base: "full", md: "170px" }}
              flex={{ base: 1, md: "none" }}
              borderRadius="md"
              borderWidth="1px"
              borderColor="gray.200"
              px={3}
              py={1}
              fontSize="sm"
              bg="white"
              _hover={{ borderColor: "gray.300" }}
              _focus={{
                borderColor: "blue.500",
                outline: "none",
                boxShadow: "0 0 0 1px rgba(66, 153, 225, 0.6)",
              }}
            >
              <option value="created_at">Newest</option>
              <option value="updated_at">Recently updated</option>
            </Box>
            <Box
              {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
              as="select"
              size="sm"
              value={direction}
              onChange={(event) =>
                handleDirectionChange(
                  (event.target as HTMLSelectElement).value as SortDirection,
                )
              }
              aria-label="Sort direction"
              width={{ base: "full", md: "150px" }}
              flex={{ base: 1, md: "none" }}
              borderRadius="md"
              borderWidth="1px"
              borderColor="gray.200"
              px={3}
              py={1}
              fontSize="sm"
              bg="white"
              _hover={{ borderColor: "gray.300" }}
              _focus={{
                borderColor: "blue.500",
                outline: "none",
                boxShadow: "0 0 0 1px rgba(66, 153, 225, 0.6)",
              }}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </Box>
          </HStack>
          <Box display={{ base: "none", md: "flex" }}>
            <HStack gap={4}>
              <ViewToggle value={viewMode} onChange={setViewMode} />
              {hasPermission("enrollments:create") && <AddEnrollment />}
            </HStack>
          </Box>
        </Flex>
      </Flex>

      {/* Class Detail Summary Card - shown when filtered by a specific class */}
      {class_template_id && classTemplate && (
        <Box
          mb={6}
          p={4}
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="lg"
          bg="gray.50"
        >
          <Text fontSize="sm" fontWeight="semibold" color="gray.500" mb={2}>
            Class Details
          </Text>
          <Text fontSize="lg" fontWeight="bold" mb={3}>
            {classTemplate.name}
          </Text>
          <SimpleGrid columns={{ base: 2, md: 4 }} gap={4}>
            <Box>
              <Text fontSize="xs" color="gray.500">Swim Level</Text>
              <Text fontSize="sm" fontWeight="medium">{classTemplate.swim_level}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">Schedule</Text>
              <Text fontSize="sm" fontWeight="medium">
                {classTemplate.day_of_week} {classTemplate.start_time}
                {classTemplate.end_time ? `–${classTemplate.end_time}` : ""}
              </Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">Location</Text>
              <Text fontSize="sm" fontWeight="medium">{classTemplate.location}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">Capacity</Text>
              <Text fontSize="sm" fontWeight="medium">
                {classTemplate.current_enrollment ?? 0}/{classTemplate.max_students}
              </Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">Price</Text>
              <Text fontSize="sm" fontWeight="medium">${Number(classTemplate.price).toFixed(2)}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">Program</Text>
              <Text fontSize="sm" fontWeight="medium">{classTemplate.program_type}</Text>
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500">Status</Text>
              <Box
                as="span"
                px={2}
                py={0.5}
                borderRadius="md"
                fontSize="xs"
                fontWeight="medium"
                bg={classTemplate.is_published ? "green.50" : "gray.100"}
                color={classTemplate.is_published ? "green.700" : "gray.600"}
              >
                {classTemplate.is_published ? "Published" : "Draft"}
              </Box>
            </Box>
            {classTemplate.instructor_id && (
              <Box>
                <Text fontSize="xs" color="gray.500">Instructor</Text>
                <Text fontSize="sm" fontWeight="medium">{classTemplate.instructor_id}</Text>
              </Box>
            )}
          </SimpleGrid>
        </Box>
      )}

      {records.length === 0 ? (
        <SwimEmptyState resource="enrollments" actionTo="/enrollments" />
      ) : (
        <>
          <Box opacity={isPlaceholderData ? 0.5 : 1}>
            {effectiveViewMode === "cards" ? (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
                {records.map((enrollment: EnrollmentResponse) => (
                  <EnrollmentCard
                    key={enrollment.id}
                    enrollment={enrollment}
                    showActions={showActions}
                  />
                ))}
              </SimpleGrid>
            ) : (
              <DataTable columns={columns} data={records} size="sm" />
            )}
          </Box>
          <Flex
            justifyContent={{ base: "center", md: "flex-end" }}
            mt={4}
            gap={2}
          >
            <Button
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled>
              Page {page} of {Math.ceil(count / PER_PAGE)}
            </Button>
            <Button
              size="sm"
              onClick={() =>
                setPage(Math.min(Math.ceil(count / PER_PAGE), page + 1))
              }
              disabled={page >= Math.ceil(count / PER_PAGE)}
            >
              Next
            </Button>
          </Flex>
        </>
      )}

      {/* Invoice Detail Modal */}
      <InvoiceDetailModal
        enrollment={selectedInvoice}
        isOpen={!!selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
        onPaymentSuccess={() => {
          // Refresh enrollment data
          window.location.reload()
        }}
      />
    </Container>
  )
}
